// /* eslint-disable */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	Backdrop,
	Box,
	CircularProgress,
	makeStyles,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

import NumberInput from 'components/Form/NumberInput';
import NumberText from 'components/Main/NumberText';
import Typography from 'components/Main/Typography';
import TableLabel from 'components/Main/TableLabel';
import InputTooltip from 'components/Main/InputTooltip';

import useCheckPermissions from 'utils/useCheckPermissions';
import ResearchTaskReportContext from 'context/ResearchTaskReportContext';
import ResearchTaskReportBContext from 'context/ResearchTaskReportBContext';
import CustomCell, { CategoryCell } from './components/CustomCell';
import CategoryTable from './CategoryTable';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import UploadButton from 'components/Buttons/UploadButton';

import API from 'apis/API';
import moment from 'moment-timezone';
import excelDateToJSDate from 'utils/excelDateToJSDate';
import isValidDate from 'utils/isValidDate';

import _ from 'lodash';

const useStyles = makeStyles((theme) => ({
	root: {
		height: 'calc(100vh - 240px)',
		marginTop: 30,
		'& .MuiTableHead-root .MuiTableCell-stickyHeader': {
			background: '#EEEEEE',
		},
		'& .MuiTableCell-root': {
			border: '1px solid #D0D0D0',
			verticalAlign: 'top',
		},
	},
	row: {
		'& .MuiTableCell-head': {
			borderBottom: '1px solid #17253F',
			top: 51,
		},
	},
	button: {
		marginRight: 10,
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1000,
		color: '#fff',
		flexDirection: 'column',
	},
	backdropText: {
		marginTop: 16,
	},
}));

const ExpensesStatements = () => {
	const { t } = useTranslation(null, {
		keyPrefix: 'Zadania badawcze - Raport B',
	});
	const { task_id: taskId, id: reportId } = useParams();
	const classes = useStyles();
	const canCreate = useCheckPermissions('can_create_report_b', true);
	const { changes } = useContext(ResearchTaskReportContext);
	const { formData, onChange, errors, selectedLanguage, disabledInput } =
		useContext(ResearchTaskReportBContext);

	const expensesStatements = formData[selectedLanguage].expenses_statements;
	const sum = useMemo(
		() => expensesStatements?.sum,
		[expensesStatements?.sum]
	);
	const [totalInterest, setTotalInterest] = useState(0);
	const [isExporting, setIsExporting] = useState(false);
	const [loadingMessage, setLoadingMessage] = useState('');
	const hiddenIdColIndex = changes.report_b_new_columns_20250303 ? 15 : 13;
	let importError = false;

	useEffect(() => {
		if (!canCreate) return;
		setTotalInterest(expensesStatements.sum.total_interest);
	}, [expensesStatements.sum.total_interest]);

	const handleChange = (e) => {
		let { value } = e.target;
		setTotalInterest(value);
		onChange('pl.expenses_statements.sum.total_interest', +value);
		onChange('en.expenses_statements.sum.total_interest', +value);
	};

	const exportTable4Xlsx = async () => {
		setLoadingMessage(t('Trwa eksport pliku, proszę czekać'));
		setIsExporting(true);
		try {
			const res = await API.researchTasks.report.exportB.table4(
				taskId,
				reportId,
				selectedLanguage
			);

			// If you want to download file automatically using link attribute.
			const link = document.createElement('a');
			const url = URL.createObjectURL(new Blob([res.data]));
			link.href = url;
			link.setAttribute('download', `${t('Raport B')}.xlsx`);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (err) {
			toast.error('Sorry, something went wrong.');
		}
		setIsExporting(false);
	};

	const importTable4Xlsx = async (e) => {
		importError = false;

		const file = e.target.files[0];
		if (!file) {
			return;
		}

		setLoadingMessage(t('Trwa import pliku, proszę czekać'));
		setIsExporting(true);

		const reader = new FileReader();
		reader.onload = (e) => {
			// Defer heavy synchronous parsing so the loader can paint first,
			// otherwise the browser appears frozen during large imports.
			setTimeout(() => {
				try {
					const data = new Uint8Array(e.target.result);
					const workbook = XLSX.read(data, { type: 'array' });
					const sheetName = workbook.SheetNames[0];
					const sheet = workbook.Sheets[sheetName];
					// Excel trims empty leading columns/rows on save, shifting the grid.
					// Force the range to start at A1 so column indices stay stable.
					const sheetRange = XLSX.utils.decode_range(sheet['!ref']);
					sheetRange.s.c = 0;
					sheetRange.s.r = 0;
					const jsonData = XLSX.utils.sheet_to_json(sheet, {
						header: 1,
						range: sheetRange,
					});

					let subTableSectionArray = [];
					let currentSubArray = [];
					let indexFlag = false;

					for (const row of jsonData) {
						if (row.length > 2 && isEntity(row[2])) indexFlag = true;
						if (isEntity(row[2]) && !getHiddenId(row)) {
							importError = true;
							break;
						}
						if (!indexFlag) continue;
						if (row[1] != undefined) {
							subTableSectionArray.push(currentSubArray);
							currentSubArray = [];
						}
						currentSubArray.push(row);
					}

					subTableSectionArray.push(currentSubArray);
					subTableSectionArray = subTableSectionArray.slice(1);

					if (subTableSectionArray.length !== 4 || importError) {
						toast.error('Nieprawidłowy format pliku z danymi');
						return;
					}

					const { dataRows: otherDirectRows, sumRows: globalSumRows } =
						splitOtherDirectSection(subTableSectionArray[3]);

					if (globalSumRows.length !== 5) {
						toast.error('Nieprawidłowy format pliku z danymi');
						return;
					}

					const plSalaryData = gettingSalaryData(
						subTableSectionArray[0],
						true
					);
					const enSalaryData = gettingSalaryData(
						subTableSectionArray[0],
						false
					);

					const plEquipmentCosts = gettingOthersData(
						subTableSectionArray[1],
						true
					);
					const enEquipmentCosts = gettingOthersData(
						subTableSectionArray[1],
						false
					);

					const plExternalServiceCosts = gettingOthersData(
						subTableSectionArray[2],
						true
					);
					const enExternalServiceCosts = gettingOthersData(
						subTableSectionArray[2],
						false
					);

					const plOtherDirectCosts = gettingOthersData(
						otherDirectRows,
						true
					);
					const enOtherDirectCosts = gettingOthersData(
						otherDirectRows,
						false
					);
					const expensesStatementsSum = gettingTotalSumData(
						globalSumRows
					);

					let plExpensesStatements = {
						sum: expensesStatementsSum,
						salary: plSalaryData,
						equipment_costs: plEquipmentCosts,
						external_service_costs: plExternalServiceCosts,
						other_direct_costs: plOtherDirectCosts,
					};

					let enExpensesStatements = {
						sum: expensesStatementsSum,
						salary: enSalaryData,
						equipment_costs: enEquipmentCosts,
						external_service_costs: enExternalServiceCosts,
						other_direct_costs: enOtherDirectCosts,
					};

					updateSum(plExpensesStatements);
					updateSum(enExpensesStatements);

					onChange('pl.expenses_statements', plExpensesStatements);
					onChange('en.expenses_statements', enExpensesStatements);
				} catch (err) {
					toast.error('Nieprawidłowy format pliku z danymi');
				} finally {
					setIsExporting(false);
				}
			}, 50);
		};
		reader.onerror = () => {
			toast.error('Sorry, something went wrong.');
			setIsExporting(false);
		};
		reader.readAsArrayBuffer(file);
	};

	function updateSum(expensesStatements) {
		// sum tables
		Object.entries(expensesStatements).forEach(([singleTableKey, singleTable]) => {
			if (singleTableKey == 'sum') {
				return;
			}

			// sum members
			singleTable.entities.forEach((singleEntity) => {
				singleEntity.members.forEach((singleMember) => {
					singleMember.sum.netto_amount = parseFloat(_.sumBy(singleMember.data, 'netto_amount'));
					singleMember.sum.vat_amount = parseFloat(_.sumBy(singleMember.data, 'vat_amount'));
					singleMember.sum.eligible_amount = parseFloat(_.sumBy(singleMember.data, 'eligible_amount'));
				});

				singleEntity.sum.netto_amount = parseFloat(_.sumBy(singleEntity.members, 'sum.netto_amount'));
				singleEntity.sum.vat_amount = parseFloat(_.sumBy(singleEntity.members, 'sum.vat_amount'));
				singleEntity.sum.eligible_amount = parseFloat(_.sumBy(singleEntity.members, 'sum.eligible_amount'));

				if (singleEntity.data) {
					singleEntity.sum.netto_amount += parseFloat(_.sumBy(singleEntity.data, 'netto_amount'));
					singleEntity.sum.vat_amount += parseFloat(_.sumBy(singleEntity.data, 'vat_amount'));
					singleEntity.sum.eligible_amount += parseFloat(_.sumBy(singleEntity.data, 'eligible_amount'));
				}
			});

			singleTable.sum.netto_amount = parseFloat(_.sumBy(singleTable.entities, 'sum.netto_amount'));
			singleTable.sum.vat_amount = parseFloat(_.sumBy(singleTable.entities, 'sum.vat_amount'));
			singleTable.sum.eligible_amount = parseFloat(_.sumBy(singleTable.entities, 'sum.eligible_amount'));
		});

		// sum global

		let _expensesStatements = _.omit(expensesStatements, 'sum');
		let _sum = _.cloneDeep(expensesStatements.sum);

		_sum.total_direct_cost = _.sumBy(_.values(_expensesStatements), item => item.sum.eligible_amount);
		_sum.total_indirect_cost = ((_expensesStatements.salary.sum.eligible_amount + _expensesStatements.other_direct_costs.sum.eligible_amount) * _sum.flat_rate) / 100;
		_sum.total = _sum.total_direct_cost + _sum.total_indirect_cost;

		expensesStatements.sum = _sum;
	}

	function gettingSalaryData(salaryData, flag) {
		const salary = { entities: [], sum: {} };
		for (const row of salaryData) {
			if (isEmptyPlaceholderRow(row)) {
				continue;
			}

			const entityIndex = Math.max(salary.entities.length - 1, 0);
			const memberIndex =
				salary.entities.length > 0
					? Math.max(
						salary.entities[entityIndex].members.length - 1,
						0
					)
					: 0;
			if (isEntity(row[2])) salary.entities.push(makeEntityFromRow(row));
			else if (isMember(row[2]) > 0) {
				salary.entities[salary.entities.length - 1].members.push(
					makeMemberFromRow(row, isMember(row[2]))
				);
			} else if (isData(row[2]) === 0) {
				salary.entities[entityIndex].members[memberIndex].data.push(
					makeDataFromRow(row, flag)
				);
			} else if (isData(row[2]) === 1) {
				salary.entities[entityIndex].members[memberIndex].sum =
					makeMemberSumFromRow(row);
			} else if (isData(row[2]) === 2) {
				salary.entities[entityIndex].sum = makeEntitySumFromRow(row);
			} else if (isData(row[2]) === 3) {
				salary.sum = makeTotalSumFromRow(row);
			}
		}
		return salary;
	}

	function gettingOthersData(otherData, flag) {
		const equipmentCosts = { entities: [], sum: {} };
		for (const row of otherData) {
			if (isGlobalSumRow(row[2]) || isEmptyPlaceholderRow(row)) {
				continue;
			}

			const entityIndex = Math.max(equipmentCosts.entities.length - 1, 0);

			if (isEntity(row[2]))
				equipmentCosts.entities.push(makeEntityFromRow(row));
			else if (isData(row[2]) === 0) {
				equipmentCosts.entities[entityIndex].data.push(
					makeDataFromRow(row, flag)
				);
			} else if (isData(row[2]) === 2) {
				equipmentCosts.entities[entityIndex].sum =
					makeEntitySumFromRow(row);
			} else if (isData(row[2]) === 3) {
				equipmentCosts.sum = makeTotalSumFromRow(row);
			}
		}
		return equipmentCosts;
	}

	function gettingTotalSumData(sumData) {
		const sum = {
			total_direct_cost: 0,
			flat_rate: 0,
			total_indirect_cost: 0,
			total: 0,
			total_interest: 0,
		};

		for (const row of sumData) {
			const label = (row[2] || '').toString().toLowerCase();
			const value = row[7]
				? parseFloat(
					row[7]
						.toString()
						.replace(/\s/g, '')
						.replace(',', '.')
				)
				: 0;

			if (label.includes('koszty bezpośrednie razem')) {
				sum.total_direct_cost = value;
			} else if (label.includes('przyznana stopa ryczałtu (%)')) {
				sum.flat_rate = value;
			} else if (label.includes('koszty pośrednie razem')) {
				sum.total_indirect_cost = value;
			} else if (label === 'razem') {
				sum.total = value;
			} else if (label.includes('suma odsetek')) {
				sum.total_interest = value;
			}
		}

		return sum;
	}

	function getHiddenId(row) {
		return row[hiddenIdColIndex] ?? row[13];
	}

	function splitOtherDirectSection(rows) {
		const dataRows = [];
		const sumRows = [];

		for (const row of rows) {
			if (isGlobalSumRow(row[2])) {
				sumRows.push(row);
			} else {
				dataRows.push(row);
			}
		}

		return { dataRows, sumRows };
	}

	function isGlobalSumRow(value) {
		if (!value) {
			return false;
		}

		const label = value.toString().toLowerCase();

		return label.includes('koszty bezpośrednie razem') ||
			label.includes('przyznana stopa ryczałtu (%)') ||
			label.includes('koszty pośrednie razem') ||
			label === 'razem' ||
			label.includes('suma odsetek');
	}

	function isEmptyPlaceholderRow(row) {
		if (!row[2] || isEntity(row[2]) || isMember(row[2]) || isData(row[2])) {
			return false;
		}

		const lp = row[2].toString().replace('.', '');
		if (isNaN(Number(lp))) {
			return false;
		}

		const hasDocument = row[3] || row[4];
		const nettoAmount = row[7]
			? parseFloat(
				row[7]
					.toString()
					.replace(/\s/g, '')
					.replace(',', '.')
			)
			: 0;
		const eligibleAmount = row[9]
			? parseFloat(
				row[9]
					.toString()
					.replace(/\s/g, '')
					.replace(',', '.')
			)
			: 0;

		return !hasDocument && !nettoAmount && !eligibleAmount;
	}

	function makeEntityFromRow(row) {
		return {
			entity_name: row[7],
			entity_id: getHiddenId(row),
			members: [],
			data: [],
			sum: {},
		};
	}

	function makeMemberFromRow(row, member_type_id) {
		return { id: getHiddenId(row), member_type_id, name: row[7], data: [], sum: {} };
	}

	function makeDataFromRow(row, flag) {
		return {
			document_number: row[3] === undefined ? '' : row[3],
			registration_number: row[4] === undefined ? '' : row[4],
			document_date:
				row[5] === undefined
					? ''
					: moment(
						isValidDate(row[5])
							? row[5]
							: excelDateToJSDate(row[5])
					).format('YYYY-MM-DD'),
			payment_date:
				row[6] === undefined
					? ''
					: moment(
						isValidDate(row[6])
							? row[6]
							: excelDateToJSDate(row[6])
					).format('YYYY-MM-DD'),
			netto_amount:
				row[7] === undefined
					? ''
					: parseFloat(
						row[7]
							.toString()
							.replace(/\s/g, '')
							.replace(',', '.')
					),
			vat_amount:
				row[8] === undefined
					? ''
					: parseFloat(
						row[8]
							.toString()
							.replace(/\s/g, '')
							.replace(',', '.')
					),
			eligible_amount:
				row[9] === undefined
					? ''
					: parseFloat(
						row[9]
							.toString()
							.replace(/\s/g, '')
							.replace(',', '.')
					),
			service_description:
				row[flag ? 10 : 11] === undefined ? '' : row[flag ? 10 : 11],

			...(changes.report_b_new_columns_20250303 ? {
				research_task_stage_nb: row[12] === undefined ? '' : row[12],
				cost_estimate_item: row[13] === undefined ? '' : row[13],
				document_issuer: row[14] === undefined ? '' : row[14],
			} : {
				document_issuer: row[12] === undefined ? '' : row[12],
			}),
		};
	}

	function makeMemberSumFromRow(row) {
		return {
			netto_amount: row[7]
				? parseFloat(
					row[7].toString().replace(/\s/g, '').replace(',', '.')
				)
				: 0,
			vat_amount: row[8]
				? parseFloat(
					row[8].toString().replace(/\s/g, '').replace(',', '.')
				)
				: 0,
			eligible_amount: row[9]
				? parseFloat(
					row[9].toString().replace(/\s/g, '').replace(',', '.')
				)
				: 0,
		};
	}

	function makeEntitySumFromRow(row) {
		return {
			netto_amount: row[7]
				? parseFloat(
					row[7].toString().replace(/\s/g, '').replace(',', '.')
				)
				: 0,
			vat_amount: row[8]
				? parseFloat(
					row[8].toString().replace(/\s/g, '').replace(',', '.')
				)
				: 0,
			eligible_amount: row[9]
				? parseFloat(
					row[9].toString().replace(/\s/g, '').replace(',', '.')
				)
				: 0,
		};
	}

	function makeTotalSumFromRow(row) {
		return {
			netto_amount: row[7]
				? parseFloat(
					row[7].toString().replace(/\s/g, '').replace(',', '.')
				)
				: 0,
			vat_amount: row[8]
				? parseFloat(
					row[8].toString().replace(/\s/g, '').replace(',', '.')
				)
				: 0,
			eligible_amount: row[9]
				? parseFloat(
					row[9].toString().replace(/\s/g, '').replace(',', '.')
				)
				: 0,
		};
	}

	function isEntity(value) {
		if (!value) return false;

		return (
			value.toString().toLowerCase() === 'NAZWA WNIOSKODAWCY (PODMIOT LIDERA)'.toLowerCase() ||
			value.toString().toLowerCase() === 'NAZWA INNEJ JEDNOSTKI'.toLowerCase()
		);
	}

	function isMember(value) {
		if (!value) {
			return 0;
		}

		if (value.toString().toLowerCase() === 'IMIĘ I NAZWISKO CZŁONKA ZESPOŁU'.toLowerCase()) return 2;
		else if (value.toString().toLowerCase() === 'IMIĘ I NAZWISKO LIDERA'.toLowerCase()) return 1;
		return 0;
	}

	function isData(value) {
		if (!value) {
			return 0;
		}

		if (value.toString().toLowerCase() === 'Suma dla osoby'.toLowerCase()) return 1;
		else if (value.toString().toLowerCase() === 'Suma dla podmiotu'.toLowerCase()) return 2;
		else if (
			value.toString().toLowerCase() === 'Suma kosztów wynagrodzeń'.toLowerCase() ||
			value.toString().toLowerCase() ===
				'Suma kosztów aparatury naukowo-badawczej, środków trwałych i wartości niematerialnych i prawnych'.toLowerCase() ||
			value.toString().toLowerCase() === 'Suma kosztów podwykonawstwa i usług obcych'.toLowerCase() ||
			value.toString().toLowerCase() === 'Suma innych kosztów bezpośrednich'.toLowerCase()
		)
			return 3;
		return 0;
	}

	return (
		<div id="research-task-report-b">
			<Backdrop className={classes.backdrop} open={isExporting}>
				<CircularProgress color="inherit" />
				<Typography variant="headBody" className={classes.backdropText}>
					{loadingMessage || t('System pracuje, proszę czekać')}
				</Typography>
			</Backdrop>
			<Box
				display={'flex'}
				justifyContent={'space-between'}
				alignItems={'center'}
			>
				<Typography variant="h4" color="primary">
					{t(
						'Tabela nr 4 - Zestawienie poniesionych wydatków związanych z realizacją Zadania Badawczego'
					)}
				</Typography>

				<Box>
					{reportId && (
						<PrimaryButton
							variant="outlined"
							className={classes.button}
							onClick={exportTable4Xlsx}
							disabled={isExporting}
						>
							Export
						</PrimaryButton>
					)}

					<UploadButton
						acceptFiles=".xlsx"
						handleCapture={importTable4Xlsx}
						disabled={isExporting}
						variant="outlined"
					>
						Import
					</UploadButton>
				</Box>
			</Box>
			<TableContainer className={classes.root}>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<CategoryCell rowSpan={2}>
								<TableLabel>{t('Kategoria kosztu')}</TableLabel>
							</CategoryCell>
							<TableCell colSpan={13}>
								<TableLabel>
									{t(
										'Koszty kwalifikowane zadania badawczego - dokumenty w podziale na kategorie kosztów'
									)}
								</TableLabel>
							</TableCell>
						</TableRow>
						<TableRow className={classes.row}>
							<TableCell>
								<TableLabel>{t('LP.')}</TableLabel>
							</TableCell>
							<TableCell>
								<TableLabel>{t('Nr dokumentu')}</TableLabel>
							</TableCell>
							<TableCell>
								<TableLabel>
									{t('Nr księgowy lub ewidencyjny')}
								</TableLabel>
							</TableCell>
							<TableCell style={{ minWidth: 130 }}>
								<TableLabel>{t('Data wystawienia')}</TableLabel>
							</TableCell>
							<TableCell style={{ minWidth: 130 }}>
								<TableLabel>{t('Data zapłaty')}</TableLabel>
							</TableCell>
							<TableCell>
								<TableLabel>{t('Kwota netto')}</TableLabel>
							</TableCell>
							<TableCell style={{ minWidth: 100 }}>
								<Box
									display="flex"
									alignItems="center"
									justifyContent="center"
								>
									<TableLabel>{t('VAT')}</TableLabel>
									<InputTooltip
										title={t(
											'VAT a w przypadku kategorii 1 Wynagrodzenia - Narzuty na wynagrodzenia'
										)}
									/>
								</Box>
							</TableCell>
							<TableCell>
								<TableLabel>
									{t('Razem koszty kwalifikowane (Finansowanie z Funduszu)')}
								</TableLabel>
							</TableCell>
							<TableCell>
								<TableLabel>
									{t('Opis wydatku (nazwa towaru lub usługi)')}
								</TableLabel>
							</TableCell>
							{changes.report_b_new_columns_20250303 && <>
								<TableCell>
									<TableLabel>
										{t('Nr etapu Zadania Badawczego')}
									</TableLabel>
								</TableCell>
								<TableCell>
									<TableLabel>
										{t('Pozycja z Kosztorysu')}
									</TableLabel>
								</TableCell>
							</>}
							<TableCell>
								<TableLabel>
									{t('Wystawca dokumentu')}
								</TableLabel>
							</TableCell>
							<TableCell width="100">
								<TableLabel>{t('Akcje')}</TableLabel>
							</TableCell>
						</TableRow>
					</TableHead>
					<CategoryTable
						categoryTitle={t('1. Wynagrodzenia')}
						categorySumTitle={t('Suma kosztów wynagrodzeń')}
						tableKey="salary"
						showMember
					/>
					<CategoryTable
						categoryTitle={t(
							'2. Koszty aparatury naukowo-badawczej, środków trwałych i wartości niematerialnych i prawnych (w tym leasing, odpisy amortyzacyjne i koszty odpłatnego korzystania)'
						)}
						categorySumTitle={t(
							'Suma kosztów aparatury naukowo-badawczej, środków trwałych i wartości niematerialnych i prawnych'
						)}
						tableKey="equipment_costs"
					/>
					<CategoryTable
						categoryTitle={t('3. Podwykonawstwo i usługi obce')}
						categorySumTitle={t(
							'Suma kosztów podwykonawstwa i usług obcych'
						)}
						tableKey="external_service_costs"
					/>
					<CategoryTable
						categoryTitle={t('4. Inne koszty bezpośrednie')}
						categorySumTitle={t(
							'Suma innych kosztów bezpośrednich'
						)}
						tableKey="other_direct_costs"
					/>
					<TableBody>
						<TableRow>
							<TableCell rowSpan={0}></TableCell>
							<CustomCell type="total" colSpan={5}>
								<TableLabel align="right">
									{t(
										'Koszty bezpośrednie razem (kat.1 + kat.2 + kat.3 + kat.4)'
									)}
								</TableLabel>
							</CustomCell>
							<CustomCell type="total" colSpan={3}>
								<NumberText
									fixedDecimalScale={true}
									value={sum?.total_direct_cost}
								/>
							</CustomCell>
						</TableRow>
						<TableRow>
							<CustomCell type="total" colSpan={5}>
								<TableLabel align="right">
									{t('Przyznana stopa ryczałtu (%)')}
								</TableLabel>
							</CustomCell>
							<CustomCell type="total" colSpan={3}>
								<NumberText
									fixedDecimalScale={true}
									value={sum?.flat_rate}
								/>
							</CustomCell>
						</TableRow>
						<TableRow>
							<CustomCell type="total" colSpan={5}>
								<TableLabel align="right">
									{t(
										'Koszty pośrednie razem (kat.1 + kat.4) x przyznana stopa ryczałtu'
									)}
								</TableLabel>
							</CustomCell>
							<CustomCell type="total" colSpan={3}>
								<NumberText
									fixedDecimalScale={true}
									value={sum?.total_indirect_cost}
								/>
							</CustomCell>
						</TableRow>
						<TableRow>
							<CustomCell type="entity" colSpan={5}>
								<TableLabel align="right">
									{t('Razem')}
								</TableLabel>
							</CustomCell>
							<CustomCell type="entity" colSpan={3}>
								<NumberText
									fixedDecimalScale={true}
									value={sum?.total}
								/>
							</CustomCell>
						</TableRow>
						<TableRow>
							<CustomCell type="total" colSpan={5}>
								<TableLabel align="right">
									{t(
										'Suma odsetek uzyskanych od kwoty finansowania zadania badawczego w całym okresie realizacji'
									)}
								</TableLabel>
							</CustomCell>
							<CustomCell type="total" colSpan={3}>
								{canCreate ? (
									<NumberInput
										value={totalInterest}
										onChange={handleChange}
										fullWidth
										error={
											errors?.expenses_statements?.sum
												?.total_interest
										}
										disabled={
											disabledInput ||
											selectedLanguage === 'en'
										}
									/>
								) : (
									<NumberText
										fixedDecimalScale={true}
										value={sum?.total_interest}
									/>
								)}
							</CustomCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		</div>
	);
};

export default ExpensesStatements;
